import { useCallback, useEffect, useState } from 'react';
import type { SpotifyAuthStatus } from '@hitster/shared';
import { useT } from './i18n.tsx';
import { Alert } from './components/common.tsx';
import { Toast } from './components/Toast.tsx';
import { SpeakerProvider } from './hooks/SpeakerProvider.tsx';
import { fetchAuthStatus } from './net/spotify-auth.ts';
import { useRoom } from './net/useRoom.ts';
import { GamePage } from './pages/GamePage.tsx';
import { HomePage } from './pages/HomePage.tsx';
import { LobbyPage } from './pages/LobbyPage.tsx';
import { ScreenPage } from './pages/ScreenPage.tsx';

/**
 * Wurzelkomponente.
 *
 * Kein Router: welcher Bildschirm sichtbar ist, folgt direkt aus dem
 * Serverzustand (kein Raum -> Startseite, Lobby -> Lobby, sonst Spiel).
 * Das haelt Browser-Navigation und Spielzustand automatisch synchron --
 * bei einem Realtime-Spiel ist der Zustand die Wahrheit, nicht die URL.
 */
export function App() {
  const t = useT();
  const api = useRoom();
  const [authStatus, setAuthStatus] = useState<SpotifyAuthStatus | null>(null);
  const [screenMode, setScreenMode] = useState(false);

  /**
   * Nur die konkret benutzte Funktion als Abhaengigkeit, nicht das
   * ganze `api`-Objekt. Dessen Identitaet wechselt bei jeder
   * Zustandsaenderung des Spiels -- der Effekt wuerde sonst bei jedem
   * Spielzug den Spotify-Status neu abrufen.
   */
  const { syncSession } = api;

  const reloadAuth = useCallback(() => {
    void fetchAuthStatus()
      .then((status) => {
        setAuthStatus(status);
        // Der Socket bekommt den OAuth-Redirect nicht mit. Ist Spotify
        // jetzt verknuepft, muss der Server seine Sicht auf diesen
        // Spieler aktualisieren -- sonst gilt er im Raum weiter als
        // "kein Spotify" und darf keinen Ton ausgeben.
        if (status.linked) syncSession();
      })
      .catch((error: unknown) => {
        // Netzfehler duerfen keine unbehandelte Promise-Ablehnung
        // erzeugen; der Verbindungsstatus wird ohnehin angezeigt.
        console.warn('[auth] Status konnte nicht geladen werden:', error);
      });
  }, [syncSession]);

  useEffect(() => {
    reloadAuth();
  }, [reloadAuth]);

  // Verknuepft ein Spieler waehrend des Spiels sein Konto, kommt er per
  // Redirect zurueck -- dann Status neu holen.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') reloadAuth();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [reloadAuth]);

  // Nach einem Reconnect kennt der Server die Spotify-Bindung wieder
  // nicht mehr, bis sie erneut gemeldet wird.
  useEffect(() => {
    if (api.connected && authStatus?.linked) syncSession();
  }, [api.connected, authStatus?.linked, syncSession]);

  const { state } = api;

  return (
    /**
     * Der SpeakerProvider MUSS oberhalb des Ansichtswechsels sitzen.
     *
     * Das Spotify-Geraet existiert nur, solange die Komponente montiert
     * ist, die das SDK haelt. Lag der Player in einer einzelnen Ansicht,
     * riss der Wechsel zur TV-Ansicht das Geraet ab -- mitten im Spiel
     * verstummte die Musik.
     */
    <SpeakerProvider api={api}>
      {/* Kurzmeldung unten links; verschiebt den Seiteninhalt nicht. */}
      {api.error && <Toast error={api.error} onClose={api.clearError} />}

      {!api.connected && state !== null && (
        <div style={{ padding: '0.5rem 1rem' }}>
          <Alert kind="warning">{t('error.disconnected')}</Alert>
        </div>
      )}

      {state === null ? (
        <HomePage api={api} authStatus={authStatus} onAuthChanged={reloadAuth} />
      ) : state.phase === 'lobby' ? (
        <LobbyPage state={state} api={api} authStatus={authStatus} />
      ) : /* Zuschauer haben keine Steuerung -- fuer sie ist die grosse
             Ansicht die einzige sinnvolle Darstellung. */
      screenMode || state.youAreSpectator ? (
        <ScreenPage state={state} api={api} log={api.log} authStatus={authStatus} />
      ) : (
        <GamePage state={state} api={api} authStatus={authStatus} />
      )}

      {/* Umschalter TV-Ansicht / Steuerung -- nur waehrend des Spiels. */}
      {state !== null && state.phase !== 'lobby' && !state.youAreSpectator && (
        <div
          style={{
            position: 'fixed',
            bottom: 'max(0.75rem, env(safe-area-inset-bottom))',
            right: '0.75rem',
            zIndex: 'var(--z-affix)',
          }}
        >
          <button
            type="button"
            className="btn btn--ghost"
            style={{ background: 'var(--surface-2)', border: '1px solid var(--line-strong)' }}
            onClick={() => setScreenMode((previous) => !previous)}
            title={t('view.screenHint')}
          >
            {screenMode ? t('view.controls') : t('view.screen')}
          </button>
        </div>
      )}
    </SpeakerProvider>
  );
}
